import Button, { TextButton } from '../PendantButton';
import { COLOR } from '../shared/shared';
import { ItemFilter } from './Shared';
import PlusIcon from './images/plus.svg';
import React, {
	ReactElement,
	Fragment,
	useState,
	useEffect,
	DependencyList,
} from 'react';

export interface ButtonProps {
	name: string;
	action: () => void;
	enabled?: boolean;
	hidden?: boolean;
	restrictionCheck?: () => string | undefined;
}

export interface ContentItemProps {
	instruction: string;
	sub_step: number;
	sub_step_count: number;
	instructionNumber: number;
	LeftButton: ButtonProps;
	RightButton: ButtonProps;
	isSummary?: boolean;
	AddButton?: ButtonProps;
	confirmAction?: ConfirmAction;
}

interface FullContentItemProps extends ContentItemProps {
	children: ReactElement;
}

interface ContentFooterProps {
	RightButton: ButtonProps;
	LeftButton: ButtonProps;
	confirmAction?: ConfirmAction;
	pCurrentItems?: number;
	pTotalItems?: number;
	pBackAction?: () => void;
	pNextAction?: () => void;
	isSummary?: boolean;
}

export function ContentFooter({
	RightButton,
	LeftButton,
	confirmAction,
	pCurrentItems,
	pTotalItems,
	pBackAction,
	pNextAction,
	isSummary,
}: ContentFooterProps) {
	const [lWarningText, lSetWarningText] = useState<string>('');
	useEffect(
		function () {
			lSetWarningText(confirmAction ? confirmAction.pWarning : '');
		},
		[confirmAction]
	);

	const lHandleRightButton = function () {
			if (confirmAction) {
				confirmAction.pAction();
				return;
			}
			if (!RightButton.enabled) return;
			if (RightButton.restrictionCheck) {
				const lRestrictMessage = RightButton.restrictionCheck();
				if (lRestrictMessage) {
					lSetWarningText(lRestrictMessage);
					return;
				}
			}

			RightButton.action();
		},
		lHandleLeftButton = function () {
			if (confirmAction) {
				confirmAction.pCancel();
				return;
			}

			if (!LeftButton.enabled) return;
			LeftButton.action();
		};

	return (
		<div className="full-width flex-row align-end flex-between padding-bottom">
			{/* Left */}
			<div className="flex-row width-30 flex-start align-center">
				{(LeftButton.enabled || confirmAction) && (
					<Button
						pClass="vw-10 margin-left"
						pAction={lHandleLeftButton}
						pClickRed={true}
						pDisabled={false}
					>
						<span> {confirmAction ? 'Cancel' : LeftButton.name} </span>
					</Button>
				)}
			</div>
			{/*  Center. Warning text or page buttons */}
			<div className="flex-row width-30 flex-center align-center padding-bottom">
				{isSummary &&
				!!pBackAction &&
				pCurrentItems !== undefined &&
				!!pNextAction ? (
					<div className="flex-row font-5 color-blue">
						<div className="">
							<TextButton
								pSpanClass="icon-arrow-left"
								pText=""
								pAction={pBackAction}
								pClickColor={COLOR.GREEN}
								pDefaultColor={COLOR.DARK_BLUE}
							/>
						</div>
						<div className=" double-padding-left double-padding-right">
							<h5 className="">
								{`${pCurrentItems + 1} of ${pTotalItems === 0 ? '1' : pTotalItems}`}
							</h5>
						</div>
						<div className="">
							<TextButton
								pSpanClass="icon-arrow-right"
								pText=""
								pAction={pNextAction}
								pClickColor={COLOR.GREEN}
								pDefaultColor={COLOR.DARK_BLUE}
							/>
						</div>
					</div>
				) : (
					<div className=" flex-center">
						<span className="color-red">{lWarningText}</span>
					</div>
				)}
			</div>

			<div className="flex-row width-30 flex-end align-center">
				{((!RightButton.hidden && RightButton.enabled) || confirmAction) && (
					<Button
						pClass={'vw-10 margin-right'}
						pAction={lHandleRightButton}
						pClickRed={false}
						pDisabled={false}
					>
						<Fragment>
							<span> {confirmAction ? 'Confirm' : RightButton.name} </span>
							{isSummary && !confirmAction && (
								<span className="icon-forward-filled"></span>
							)}
						</Fragment>
					</Button>
				)}
			</div>
		</div>
	);
}

export default function ContentItem({ children }: FullContentItemProps) {
	return <div className="full-size">{children}</div>;
}

export interface ConfirmAction {
	pAction: () => void;
	pCancel: () => void;
	pWarning: string;
}

export type ConfirmTrigger = (pAction: () => void, pWarning: string) => void;

export const useConfirm = (
	pDeps?: DependencyList | undefined // cancel confirm dialog when deps change.
): [ConfirmTrigger, ConfirmAction | undefined] => {
	const [lConfirmAction, lSetConfirmAction] = useState<
			ConfirmAction | undefined
		>(void 0),
		lCancelConfirm = function () {
			lSetConfirmAction(void 0);
		},
		lTriggerConfirm = function (pAction: () => void, pWarning: string) {
			lSetConfirmAction({
				pWarning,
				pAction: function () {
					lCancelConfirm();
					pAction();
				},
				pCancel: lCancelConfirm,
			});
		};
	useEffect(function () {
		lCancelConfirm();
	}, pDeps);
	return [lTriggerConfirm, lConfirmAction];
};
